/**
 * Sell Treez store config API (Open Search–derived base URL).
 * Env vars align with CRA: `REACT_APP_*` (see `.env`).
 */

/**
 * Builds base URL (origin + first path segment) from an endpoint URL.
 * First segment is e.g. `develop` or `prod` (e.g. `.../develop/product/search`).
 */
function getSellTreezConfigBaseUrlFromEndpoint(
    endpointUrl: string | undefined,
): string | null {
    if (!endpointUrl) {
        return null;
    }

    try {
        const { origin, pathname } = new URL(endpointUrl);
        const firstPathSegment = pathname.split("/").filter(Boolean)[0];

        if (firstPathSegment === "develop" || firstPathSegment === "prod") {
            return `${origin}/${firstPathSegment}`;
        }

        return origin;
    } catch {
        return null;
    }
}

type SellTreezStoreConfigErrorCause = {
    status: number;
    res: unknown;
    storeShortName: string;
};

/**
 * Fetches JSON config for a Treez dispensary short name from the Sell Treez config route.
 */
async function getSellTreezStoreConfig(
    storeShortName: string,
): Promise<unknown> {
    const openSearchEndpoint = process.env.REACT_APP_OPEN_SEARCH_ENDPOINT;
    const openSearchApiKey = process.env.REACT_APP_OPEN_SEARCH_API_KEY ?? "";

    const sellTreezApiUrl =
        getSellTreezConfigBaseUrlFromEndpoint(openSearchEndpoint);

    if (!sellTreezApiUrl) {
        throw new Error(
            "getSellTreezStoreConfig: REACT_APP_OPEN_SEARCH_ENDPOINT is not set or invalid",
        );
    }

    const url = `${sellTreezApiUrl}/config/${encodeURIComponent(
        storeShortName,
    )}`;

    const response = await fetch(url, {
        headers: {
            "X-Api-Key": openSearchApiKey,
            "Content-Type": "application/json",
        },
    }).catch(reason => Promise.reject(reason));

    const data = await response.json().catch(() => ({}));

    if (response.ok) {
        return data;
    }

    const err = new Error(
        `Sell Treez store config failed (${response.status}) for "${storeShortName}"`,
    ) as Error & {
        cause?: SellTreezStoreConfigErrorCause;
    };
    err.cause = {
        status: response.status,
        res: data,
        storeShortName,
    };
    throw err;
}

export { getSellTreezConfigBaseUrlFromEndpoint, getSellTreezStoreConfig };
